import { useCallback, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isFunction } from '../../utils/TypeUtils';
import CopyMessageActionButton from './internal/CopyMessageActionButton';
import InlineFeedbackPopover from './internal/InlineFeedbackPopover';
import { useTracker } from '../../hooks/useTracker';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import InteractionIconButton from './internal/InteractionIconButton';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const POSITIVE_FEEDBACK = 1;
const NEGATIVE_FEEDBACK = -1;
const InlineFeedback = ({
  hideCopyButton = false,
  copyMessage,
  isFeedbackDisabled = false,
  feedbackDisabledTooltip,
  messageId,
  onPositiveFeedback,
  onNegativeFeedback,
  usageTracker,
  usageTrackerAppLocationKey,
  initialVote,
  negativeFeedbackPopoverTitle,
  negativeFeedbackPopoverBody,
  negativeFeedbackPopoverBodyOptions,
  negativeFeedbackPopoverTextAreaPlaceholder,
  callOnNegativeFeedbackImmediately = false,
  positiveFeedbackPopoverTitle,
  positiveFeedbackPopoverBody,
  positiveFeedbackPopoverBodyOptions,
  positiveFeedbackPopoverTextAreaPlaceholder,
  threadId = 0,
  usePositiveFeedbackPopover = false,
  useNegativeFeedbackPopover = true,
  onCopyMessage,
  flexStyleProps
}) => {
  const {
    aiTracker
  } = useTracker(usageTracker, usageTrackerAppLocationKey);
  const [draftVote, setDraftVote] = useState(initialVote);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const [positiveFeedbackPopoverOpen, setPositiveFeedbackPopoverOpen] = useState(false);
  const handlePositiveFeedback = useCallback(() => {
    const isPositiveFeedbackSelected = draftVote === POSITIVE_FEEDBACK;
    if (isPositiveFeedbackSelected) {
      if (aiTracker) {
        aiTracker.track('Ai Components Ui Library', 'Inlinefeedbackinteraction', {
          action: 'prompt_upvote_removed'
        });
      }
      setDraftVote(null);
    } else {
      if (aiTracker) {
        aiTracker.track('Ai Components Ui Library', 'Inlinefeedbackinteraction', {
          action: 'prompt_upvoted'
        });
      }
      setDraftVote(POSITIVE_FEEDBACK);
      setPositiveFeedbackPopoverOpen(true);
      if (isFunction(onPositiveFeedback)) {
        onPositiveFeedback({
          messageId,
          threadId,
          vote: 1
        });
      }
    }
  }, [draftVote, messageId, onPositiveFeedback, aiTracker, threadId]);
  const handleNegativeFeedbackClick = useCallback(() => {
    const isNegativeFeedbackSelected = draftVote === NEGATIVE_FEEDBACK;
    if (isNegativeFeedbackSelected) {
      if (aiTracker) {
        aiTracker.track('Ai Components Ui Library', 'Inlinefeedbackinteraction', {
          action: 'prompt_downvote_removed'
        });
      }
      setDraftVote(null);
    } else {
      if (aiTracker) {
        aiTracker.track('Ai Components Ui Library', 'Inlinefeedbackinteraction', {
          action: 'prompt_downvoted'
        });
      }
      setDraftVote(NEGATIVE_FEEDBACK);
      setFeedbackPopoverOpen(true);
      /** callOnNegativeFeedbackImmediately was added to be reverse compatible as this change will send twice as many feedback events */
      if (callOnNegativeFeedbackImmediately && isFunction(onNegativeFeedback)) {
        onNegativeFeedback({
          messageId,
          threadId,
          options: [],
          feedbackMessage: '',
          vote: -1
        });
      }
    }
  }, [draftVote, aiTracker, messageId, onNegativeFeedback, threadId, callOnNegativeFeedbackImmediately]);
  const handleClose = useCallback(() => {
    setFeedbackPopoverOpen(false);
  }, []);
  const handlePositiveFeedbackClose = useCallback(() => {
    setPositiveFeedbackPopoverOpen(false);
  }, []);
  const handleSubmit = ({
    options,
    message: feedbackMessage
  }) => {
    setFeedbackPopoverOpen(false);
    if (isFunction(onNegativeFeedback)) {
      onNegativeFeedback({
        messageId,
        threadId,
        options,
        feedbackMessage,
        vote: -1
      });
    }
  };
  const handlePositiveFeedbackSubmit = ({
    options,
    message: feedbackMessage
  }) => {
    setPositiveFeedbackPopoverOpen(false);
    if (isFunction(onPositiveFeedback)) {
      onPositiveFeedback({
        messageId,
        threadId,
        options,
        feedbackMessage,
        vote: 1
      });
    }
  };
  const PositiveFeedbackTooltip = () => {
    if (isFeedbackDisabled) {
      return feedbackDisabledTooltip !== null && feedbackDisabledTooltip !== void 0 ? feedbackDisabledTooltip : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiComponentsUiLibrary.components.inlineFeedback.disabledFeedback.tooltip"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiComponentsUiLibrary.components.inlineFeedback.positiveFeedback.tooltip"
    });
  };
  const NegativeFeedbackTooltip = () => {
    if (isFeedbackDisabled) {
      return feedbackDisabledTooltip !== null && feedbackDisabledTooltip !== void 0 ? feedbackDisabledTooltip : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiComponentsUiLibrary.components.inlineFeedback.disabledFeedback.tooltip"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiComponentsUiLibrary.components.inlineFeedback.negativeFeedback.tooltip"
    });
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Flex, Object.assign({
      "data-test-id": "inline-feedback",
      alignItems: "center"
    }, flexStyleProps, {
      children: [/*#__PURE__*/_jsx(InlineFeedbackPopover, {
        popoverOpen: usePositiveFeedbackPopover ? positiveFeedbackPopoverOpen : false,
        onSubmit: handlePositiveFeedbackSubmit,
        onCancel: handlePositiveFeedbackClose,
        popoverTitle: positiveFeedbackPopoverTitle || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.positiveDialogHeader"
        }),
        popoverBody: positiveFeedbackPopoverBody,
        negativeFeedbackPopoverBodyOptions: positiveFeedbackPopoverBodyOptions,
        textAreaPlaceholder: positiveFeedbackPopoverTextAreaPlaceholder,
        children: /*#__PURE__*/_jsx(Tooltip, {
          delay: BREEZE_TOOLTIP_DELAY,
          placement: "bottom",
          Content: PositiveFeedbackTooltip,
          children: /*#__PURE__*/_jsx(InteractionIconButton, {
            ariaLabel: unescapedText('aiComponentsUiLibrary.components.inlineFeedback.positiveFeedback.ariaLabel'),
            dataTestId: "positive-feedback-button",
            checked: draftVote === POSITIVE_FEEDBACK,
            "aria-pressed": draftVote === POSITIVE_FEEDBACK,
            disabled: isFeedbackDisabled,
            icon: "thumbsUp",
            onClick: handlePositiveFeedback
          })
        })
      }), /*#__PURE__*/_jsx(InlineFeedbackPopover, {
        popoverOpen: useNegativeFeedbackPopover ? feedbackPopoverOpen : false,
        onSubmit: handleSubmit,
        onCancel: handleClose,
        popoverTitle: negativeFeedbackPopoverTitle || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.dialogHeader"
        }),
        popoverBody: negativeFeedbackPopoverBody,
        negativeFeedbackPopoverBodyOptions: negativeFeedbackPopoverBodyOptions,
        textAreaPlaceholder: negativeFeedbackPopoverTextAreaPlaceholder,
        children: /*#__PURE__*/_jsx(Tooltip, {
          delay: BREEZE_TOOLTIP_DELAY,
          placement: "bottom",
          Content: NegativeFeedbackTooltip,
          children: /*#__PURE__*/_jsx(InteractionIconButton, {
            ariaLabel: unescapedText('aiComponentsUiLibrary.components.inlineFeedback.negativeFeedback.ariaLabel'),
            dataTestId: "negative-feedback-button",
            checked: draftVote === NEGATIVE_FEEDBACK,
            "aria-pressed": draftVote === NEGATIVE_FEEDBACK,
            disabled: isFeedbackDisabled,
            icon: "thumbsDown",
            onClick: handleNegativeFeedbackClick
          })
        })
      }), !hideCopyButton && copyMessage && /*#__PURE__*/_jsx(CopyMessageActionButton, {
        message: copyMessage,
        usageTracker: aiTracker,
        onCopyMessage: onCopyMessage
      })]
    }))
  });
};
InlineFeedback.displayName = 'InlineFeedback';
export default InlineFeedback;